import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { Box, Grid } from "@mui/system";
import { AUFlagIcons } from "assets/styles/icons";
import { getLoanTypeValue } from "context/ProgressProvider";
import { Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { BrokersService } from "services/BrokersService";
import {
  AppFormikTextField,
  AppPrimaryButton,
  AppSecondaryButton,
} from "shared/components/AppComponents";
import { RoleCode } from "shared/helpers/Constants";
import { RegisterSchema } from "shared/helpers/Schema";
import Utils from "shared/helpers/Utils";
import { AuthActions } from "state/auth/Action";
import { ProgressActions } from "state/progress/Action";

export const DialogBrokerApplications = ({
  dialogCreateLoan,
  newLoanType,
  setNewLoanType,
  setDialogCreateLoan,
  callbackCreate,
  getAllApplications,
}) => {
  const dispatch = useDispatch();
  const authInfo = useSelector((state: any) => state.authState);
  const brokerDetails = useSelector(
    (state: any) => state.authState.brokerDetails,
  );

  const handleCreateAccount = (formValues, formik, callback) => {
    BrokersService.createApplication({
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      email: formValues.email,
      phoneNumber: formValues.phoneNumber.replace(/\s+/g, ""),
      loanType: parseFloat(newLoanType),
      createdByRole: RoleCode.BROKER,
      createdByBroker: formValues.brokerId,
    })
      .then((response: any) => {
        console.log(response);
        const data = response.data.data;
        const switchToken = new Promise((resolve, reject) => {
          let LoanType = getLoanTypeValue(data.loanType);
          dispatch(ProgressActions.switchLoanType(LoanType));

          const prevLoanCount = brokerDetails?.loanCount ?? 0;

          dispatch(
            AuthActions.setAuthData({
              accessToken: data.accessToken,
              refreshToken: data.refreshToken,
              selectedLoan: data.loanId,
              brokerDetails: {
                ...brokerDetails,
                loanCount: prevLoanCount + 1,
              },
            }),
          );

          resolve(true);
        });
        switchToken.then(() => {
          callback(data);
        });
      })
      .catch((error: any) => {
        formik.setSubmitting(false);
        Utils.handleErrorResponse(error);
      })
      .finally(() => {});
  };

  return (
    <>
      <Dialog open={dialogCreateLoan !== null} fullWidth>
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h2">
              {newLoanType
                ? `Enter the customer’s details for ${
                    newLoanType === "0" ? "Secured Business Loan" : "R&D Loan"
                  }`
                : "Select new applications"}
            </Typography>
          </Box>
        </DialogTitle>
        <Formik
          enableReinitialize
          validationSchema={RegisterSchema(dialogCreateLoan?.email || null)}
          validateOnMount
          onSubmit={(formValues, formik) => {
            console.log(formValues);
            handleCreateAccount(
              {
                ...formValues,
                brokerId: dialogCreateLoan.userId,
              },
              formik,
              callbackCreate,
            );
          }}
          initialValues={{
            firstName: null,
            lastName: null,
            email: null,
            phoneNumber: "+61 ",
          }}
        >
          {({ handleSubmit, errors, isSubmitting }) => {
            return (
              <form autoComplete="off">
                <DialogContent>
                  {!newLoanType && (
                    <FormControl>
                      <RadioGroup
                        aria-labelledby="loantype-radio-buttons-group-label"
                        value={newLoanType}
                        onChange={(e) => {
                          setNewLoanType(e.target.value);
                        }}
                        name="radio-buttons-group"
                      >
                        <FormControlLabel
                          value={0}
                          control={<Radio />}
                          label="Secured Business Loan"
                        />
                        <FormControlLabel
                          value={1}
                          control={<Radio />}
                          label="R&D Loan"
                        />
                      </RadioGroup>
                    </FormControl>
                  )}

                  {newLoanType && (
                    <>
                      <Box>
                        <Grid container spacing={4}>
                          <Grid size={{ xs: 12, md: 6 }} component="div">
                            <AppFormikTextField
                              placeholder="First name"
                              label="First name"
                              name="firstName"
                              outerLabel
                              className="ezifin-default-field-dialog"
                            />
                          </Grid>

                          <Grid size={{ xs: 12, md: 6 }} component="div">
                            <AppFormikTextField
                              placeholder="Last name"
                              label="Last name"
                              name="lastName"
                              outerLabel
                              className="ezifin-default-field-dialog"
                            />
                          </Grid>
                        </Grid>
                      </Box>

                      <Box mt={3}>
                        <AppFormikTextField
                          placeholder="Email address"
                          label="Email address"
                          name="email"
                          outerLabel
                          className="ezifin-default-field-dialog"
                          blockedAndReadOnly={
                            authInfo.userRole !== 1 && authInfo.isUserLoggedIn
                              ? true
                              : false
                          }
                        />
                      </Box>

                      <Box mt={3}>
                        <AppFormikTextField
                          placeholder="Phone number"
                          label="Phone number"
                          name="phoneNumber"
                          outerLabel
                          className="ezifin-default-field-dialog"
                          slotProps={{
                            input: {
                              startAdornment: (
                                <InputAdornment position="start">
                                  <AUFlagIcons />
                                </InputAdornment>
                              ),
                            },
                          }}
                          type="customAUPhone"
                        />
                      </Box>
                    </>
                  )}
                </DialogContent>
                <DialogActions sx={{ gap: "12px" }}>
                  <AppSecondaryButton
                    title="Cancel"
                    className="btn-small"
                    onClick={(e) => {
                      setDialogCreateLoan(null);
                      e.preventDefault();
                      e.stopPropagation();
                      if (brokerDetails.loanCount === 0) {
                        dispatch(AuthActions.resetStore({}));
                      }
                      if (brokerDetails.loanCount > 0) {
                        getAllApplications(brokerDetails.userId);
                      }
                    }}
                    fullWidth
                    isLoading={false}
                    loadingText="Please wait.."
                    disabled={false}
                  />
                  <AppPrimaryButton
                    title="Continue"
                    className="btn-small"
                    onClick={handleSubmit}
                    fullWidth
                    isLoading={isSubmitting}
                    loadingText="Please wait.."
                    disabled={isSubmitting || Object.keys(errors).length > 0}
                  />
                </DialogActions>
              </form>
            );
          }}
        </Formik>
      </Dialog>
    </>
  );
};

export default DialogBrokerApplications;
